/**
 * ThreadView - Full thread view with messages, input, and header
 * Shows conversation history with typing indicators, read receipts, and offline queue
 */
'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  MoreVertical, Users, UsersRound, Search, Pin,
  Settings, UserPlus, Bell, BellOff, Trash2, X, LogOut,
  Phone, Video, Calendar, Bot
} from 'lucide-react';
import MessageBubble from './MessageBubble';
import MessageInput from './MessageInput';
import MemberDetailsModal, { type MemberDetails } from './MemberDetailsModal';
import ProposalSendDialog from '@/app/client-zone/modules/total-operations/components/proposals/ProposalSendDialog';
import { useAiProposalManagerSettings } from '@/hooks/useAiProposalManagerSettings';
import { authService } from '@/lib/authService';
import { notify } from '@/lib/notifications';
import type { ClientThread, ThreadMessage, ThreadMember, TypingUser, Attachment, PinnedMention } from './types';
import type { Proposal } from '@/lib/proposals/proposalTypes';
import offlineMessages from '@/lib/offlineMessages';
import { useTheme } from '@/contexts/ThemeContext';
import { isThreadTypeJob, isThreadTypeSchedule } from '@/lib/messageSoundPreferences';
import {
  isAiOperationsManagerThread,
} from '@/lib/operations/aiOperationsManagerConfig';
import {
  isAiProposalManagerThread,
} from '@/lib/operations/aiProposalManagerConfig';
import {
  AI_OPERATIONS_PARTICIPANT_OPTION,
  AI_PROPOSAL_PARTICIPANT_OPTION,
  type SpecialisedAiParticipantOption,
} from '@/lib/operations/specializedAiParticipants';

type ReviewAction = 'acknowledge' | 'ai_hold' | 'resume_review' | 'resolve';
type ThreadMemberWithOptionalRole = ThreadMember & { role?: string };
type ThreadMessageWithLegacyId = ThreadMessage & { message_id?: string };

interface ThreadViewProps {
  thread: ClientThread;
  messages: ThreadMessage[];
  currentUserId: number;
  currentUserName: string;
  
  // Events
  onBack: () => void;
  onSendMessage: (content: string, attachments: Attachment[], replyToId?: string, mentions?: number[]) => void;
  onSendVoiceNote?: (blob: Blob, duration: number) => void;
  onShareLocation?: () => void;
  onReaction: (messageId: string, emoji: string) => void;
  onDeleteMessage?: (messageId: string) => void;
  onEditMessage?: (messageId: string, newContent: string) => void;
  onAddToTasks?: (messageId: string) => void;
  hasTaskReference?: (messageId: string) => boolean;
  addingTaskId?: string | null;
  onReviewAction?: (message: ThreadMessage, action: ReviewAction) => Promise<{ review_status?: string; appointment_status?: string } | void> | { review_status?: string; appointment_status?: string } | void;
  onUploadAttachment: (file: File) => Promise<Attachment | null>;
  onTyping: () => void;
  onStoppedTyping: () => void;
  
  // Thread settings
  onAddMember?: () => void;
  onViewMembers?: () => void;
  onMuteThread?: () => void;
  onArchiveThread?: () => void;
  onDeleteThread?: () => void;
  onLeaveThread?: () => void;
  onOpenSettings?: () => void;
  
  // Calling (for team/group threads)
  onStartVoiceCall?: () => void;
  onStartVideoCall?: () => void;
  onStartMeeting?: (callType: 'voice' | 'video') => void; // Meeting = notify only, no ring
  canMakeCalls?: boolean;
  
  // Mention handling
  onDismissMention?: (messageId: string) => void;
  onDismissAllMentions?: () => void;
  
  // State
  typing?: TypingUser[];
  pinnedMentions?: PinnedMention[];
  isDark?: boolean;
  isLoading?: boolean;
  sending?: boolean;
  uploadingAttachment?: boolean;
  sharingLocation?: boolean;
  aiParticipantThinking?: boolean;
  isFullPage?: boolean;
  isAdmin?: boolean;
  clientLogo?: string | null;
  
  // Share mode - pre-filled content from share intent
  initialAttachments?: Attachment[];
  initialText?: string;
  isShareMode?: boolean;
  onShareComplete?: () => void;
}

export default function ThreadView({
  thread,
  messages,
  currentUserId,
  currentUserName,
  onBack,
  onSendMessage,
  onSendVoiceNote,
  onShareLocation,
  onReaction,
  onDeleteMessage,
  onEditMessage,
  onAddToTasks,
  hasTaskReference,
  addingTaskId,
  onReviewAction,
  onUploadAttachment,
  onTyping,
  onStoppedTyping,
  onAddMember,
  onViewMembers,
  onMuteThread,
  onArchiveThread,
  onDeleteThread,
  onLeaveThread,
  onOpenSettings,
  onStartVoiceCall,
  onStartVideoCall,
  onStartMeeting,
  canMakeCalls = false,
  onDismissMention,
  onDismissAllMentions,
  typing = [],
  pinnedMentions = [],
  isDark = false,
  isLoading = false,
  sending = false,
  uploadingAttachment = false,
  sharingLocation = false,
  aiParticipantThinking = false,
  isFullPage = false,
  isAdmin = false,
  clientLogo = null,
  initialAttachments,
  initialText,
  isShareMode = false,
  onShareComplete
}: ThreadViewProps) {
  const { isDeliveryChannelEnabled } = useAiProposalManagerSettings();
  // Theme
  const { currentTheme, clientBranding } = useTheme();
  const primaryColor = clientBranding?.primary_color || currentTheme?.colors?.primary || '#3b82f6';
  
  // State
  const [messageText, setMessageText] = useState('');
  const [pendingAttachments, setPendingAttachments] = useState<Attachment[]>([]);
  const [replyTo, setReplyTo] = useState<ThreadMessage | null>(null);
  const [selectedMentions, setSelectedMentions] = useState<number[]>([]);
  const [showThreadMenu, setShowThreadMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [offlineMessages_, setOfflineMessages] = useState<ThreadMessage[]>([]);
  const [showCallMenu, setShowCallMenu] = useState(false); // Call/Meeting options dropdown
  const [selectedMemberDetails, setSelectedMemberDetails] = useState<MemberDetails | null>(null); // Member details modal
  const [proposalToEmail, setProposalToEmail] = useState<Proposal | null>(null);
  const [proposalEmailLoadingId, setProposalEmailLoadingId] = useState<string | null>(null);
  
  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const callMenuRef = useRef<HTMLDivElement>(null);
  const hasScrolledToBottomRef = useRef(false);
  const wasNearBottomRef = useRef(true);
  const previousRenderedMessageCountRef = useRef(0);

  const scrollThreadToBottom = useCallback((behavior: ScrollBehavior = 'auto') => {
    requestAnimationFrame(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior, block: 'end' });
      } else if (messagesContainerRef.current) {
        const container = messagesContainerRef.current;
        container.scrollTo({ top: container.scrollHeight, behavior });
      }
      hasScrolledToBottomRef.current = true;
    });
  }, []);
  
  // Get thread ID (could be id or thread_id depending on data source)
  const threadId = thread.id || thread.thread_id;
  const canEmailProposalFromThread = isAiProposalManagerThread(thread) && isDeliveryChannelEnabled('email');
  
  // Track if we've initialized share mode attachments (to prevent re-applying)
  const shareInitializedRef = useRef(false);
  
  // Handle initial attachments and text from share mode - runs BEFORE draft loading
  // Use ref to ensure we only set attachments ONCE per share session
  useEffect(() => {
    // Only set attachments if we haven't already initialized AND we're in share mode with attachments
    if (isShareMode && initialAttachments && initialAttachments.length > 0 && !shareInitializedRef.current) {
      console.log('[ThreadView] 📤 Setting initial attachments from share:', initialAttachments.length);
      setPendingAttachments(initialAttachments);
      shareInitializedRef.current = true;
    } else if (isShareMode && initialAttachments && initialAttachments.length > 0 && shareInitializedRef.current) {
      console.log('[ThreadView] 📤 Share attachments already initialized, skipping duplicate set');
    }
    
    // Only set initial text once as well (but don't use ref for text - just check if empty)
    if (isShareMode && initialText && !messageText) {
      console.log('[ThreadView] 📤 Setting initial text from share');
      setMessageText(initialText);
    }
  }, [isShareMode, initialAttachments, initialText, messageText]);
  
  // Reset share initialized ref when thread changes (but NOT attachments - they persist during share session)
  useEffect(() => {
    // Don't reset shareInitializedRef during share mode - we want attachments to persist
    if (!isShareMode) {
      shareInitializedRef.current = false;
    }
  }, [threadId, isShareMode]);
  
  // Load offline queued messages for this thread
  useEffect(() => {
    const loadOfflineMessages = async () => {
      if (!threadId) return;
      try {
        const pending = await offlineMessages.getPendingMessagesForThread(threadId);
        const asThreadMessages = pending.map(msg => 
          offlineMessages.toThreadMessage(msg, currentUserName) as ThreadMessage
        );
        setOfflineMessages(asThreadMessages);
      } catch (error) {
        console.error('Failed to load offline messages:', error);
      }
    };
    
    loadOfflineMessages();
  }, [threadId, currentUserName, messages]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const updateNearBottom = () => {
      wasNearBottomRef.current = container.scrollHeight - container.scrollTop - container.clientHeight < 120;
    };

    updateNearBottom();
    container.addEventListener('scroll', updateNearBottom, { passive: true });

    return () => container.removeEventListener('scroll', updateNearBottom);
  }, [threadId]);
  
  const handleComposerFocus = useCallback(() => {
    scrollThreadToBottom('auto');

    setTimeout(() => {
      scrollThreadToBottom('auto');
    }, 180);
  }, [scrollThreadToBottom]);
  
  // Reset scroll flag when thread changes
  useEffect(() => {
    hasScrolledToBottomRef.current = false;
    wasNearBottomRef.current = true;
    previousRenderedMessageCountRef.current = 0;
  }, [threadId]);
  
  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowThreadMenu(false);
      }
      if (callMenuRef.current && !callMenuRef.current.contains(e.target as Node)) {
        setShowCallMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleOpenProposalEmail = useCallback(async (proposalId: string) => {
    if (!proposalId) {
      return;
    }

    if (!isDeliveryChannelEnabled('email')) {
      notify.error('Proposal email delivery is disabled for this client');
      return;
    }

    try {
      setProposalEmailLoadingId(proposalId);

      const token = authService.getTokens()?.access_token;
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`/api/v1/proposals?proposal_id=${encodeURIComponent(proposalId)}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const payload = await response.json().catch(() => null);
      if (!response.ok || !payload?.success || !payload?.data) {
        throw new Error(payload?.error || 'Failed to load proposal');
      }

      setProposalToEmail(payload.data as Proposal);
    } catch (error) {
      console.error('[ThreadView] Failed to open proposal email dialog:', error);
      notify.error(error instanceof Error ? error.message : 'Failed to load proposal');
    } finally {
      setProposalEmailLoadingId(null);
    }
  }, [isDeliveryChannelEnabled]);
  
  // Load draft on mount - SKIP if in share mode with attachments
  useEffect(() => {
    const loadDraft = async () => {
      if (!threadId) return;
      
      // Skip loading draft if we're in share mode and have share attachments
      // The share attachments should take priority
      if (isShareMode && shareInitializedRef.current) {
        console.log('[ThreadView] 📤 Skipping draft load - share mode active with attachments');
        return;
      }
      
      const draft = await offlineMessages.getDraft(threadId);
      if (draft) {
        setMessageText(draft.content);
        if (draft.attachments) setPendingAttachments(draft.attachments);
        if (draft.reply_to) setReplyTo(draft.reply_to);
      }
    };
    loadDraft();
  }, [threadId, isShareMode]);
  
  // Auto-save draft on changes
  useEffect(() => {
    const saveDraft = async () => {
      if (!threadId) return;
      if (messageText.trim() || pendingAttachments.length > 0 || replyTo) {
        await offlineMessages.saveDraft(threadId, messageText, pendingAttachments, replyTo || undefined);
      }
    };
    
    const debounce = setTimeout(saveDraft, 1000);
    return () => clearTimeout(debounce);
  }, [threadId, messageText, pendingAttachments, replyTo]);
  
  // Handle attachment upload
  const handleAttachmentUpload = useCallback(async (file: File): Promise<Attachment | null> => {
    const attachment = await onUploadAttachment(file);
    if (attachment) {
      setPendingAttachments(prev => [...prev, attachment]);
    }
    return attachment;
  }, [onUploadAttachment]);
  
  // Handle send
  const handleSend = useCallback(() => {
    if (!messageText.trim() && pendingAttachments.length === 0) return;
    
    // Start with explicitly selected mentions (from dropdown)
    const allMentions = new Set(selectedMentions);
    
    // Also parse @mentions from message text and match against thread members
    const mentionRegex = /@([^@\n]+?)(?=\s|$|@)/g;
    let match;
    const threadMembers = thread.members || [];
    
    while ((match = mentionRegex.exec(messageText)) !== null) {
      const mentionedName = match[1].trim().toLowerCase();
      // Find matching member by display name
      const matchedMember = threadMembers.find(m => 
        m.display_name.toLowerCase() === mentionedName ||
        m.display_name.toLowerCase().startsWith(mentionedName)
      );
      if (matchedMember) {
        allMentions.add(matchedMember.user_id);
      }
    }
    
    const replyToMessageId = replyTo?.id || replyTo?._id || (replyTo as ThreadMessageWithLegacyId | null)?.message_id;
    onSendMessage(messageText, pendingAttachments, replyToMessageId, Array.from(allMentions));
    setMessageText('');
    setPendingAttachments([]);
    setReplyTo(null);
    setSelectedMentions([]);
    
    // Clear draft
    if (threadId) {
      offlineMessages.clearDraft(threadId);
    }
    
    // Notify share mode is complete
    if (isShareMode && onShareComplete) {
      console.log('[ThreadView] 📤 Share message sent, notifying completion');
      onShareComplete();
    }
  }, [messageText, pendingAttachments, replyTo, onSendMessage, threadId, selectedMentions, thread.members, isShareMode, onShareComplete]);
  
  // Handle voice note
  const handleVoiceNote = useCallback((blob: Blob, duration: number) => {
    onSendVoiceNote?.(blob, duration);
  }, [onSendVoiceNote]);
  
  // Handle reply
  const handleReply = useCallback((message: ThreadMessage) => {
    setReplyTo(message);
  }, []);
  
  // Remove attachment
  const handleRemoveAttachment = useCallback((attachmentId: string) => {
    setPendingAttachments(prev => prev.filter(a => a.id !== attachmentId));
  }, []);
  
  // Scroll to a specific message (for pinned mentions)
  const scrollToMessage = useCallback((messageId: string) => {
    const element = document.getElementById(`msg-${messageId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Briefly highlight the message
      element.classList.add('ring-2', 'ring-yellow-400');
      setTimeout(() => {
        element.classList.remove('ring-2', 'ring-yellow-400');
      }, 2000);
    }
  }, []);
  
  const normalizeMessageId = (message: Partial<ThreadMessage>) => {
    const legacyMessage = message as Partial<ThreadMessageWithLegacyId>;
    const rawId = legacyMessage._id || legacyMessage.id || legacyMessage.message_id || legacyMessage.temp_id;
    return rawId ? String(rawId) : null;
  };

  const getSenderId = (message: Partial<ThreadMessage>) => {
    if (message.sender_user_id !== undefined && message.sender_user_id !== null) {
      return String(message.sender_user_id);
    }

    if (message.sender_id !== undefined && message.sender_id !== null) {
      return String(message.sender_id);
    }

    return null;
  };

  const attachmentsMatch = (a: Attachment[] | undefined, b: Attachment[] | undefined) => {
    if (!a?.length || !b?.length || a.length !== b.length) {
      return false;
    }

    const attachmentKeys = new Set(a.map(att => `${att.url}|${att.originalFilename || ''}|${att.size || 0}`));
    return b.every(att => attachmentKeys.has(`${att.url}|${att.originalFilename || ''}|${att.size || 0}`));
  };

  const closeInTime = (a?: string, b?: string) => {
    if (!a || !b) return false;
    return Math.abs(new Date(a).getTime() - new Date(b).getTime()) < 30000;
  };

  const reconciledOfflineMessages = offlineMessages_.filter(offlineMessage => {
    const offlineId = normalizeMessageId(offlineMessage);
    const offlineSenderId = getSenderId(offlineMessage);
    const offlineTime = offlineMessage.created_at || offlineMessage.timestamp;

    return !messages.some(serverMessage => {
      const serverId = normalizeMessageId(serverMessage);
      const serverSenderId = getSenderId(serverMessage);
      const serverTime = serverMessage.created_at || serverMessage.timestamp;

      if (offlineId && serverId && offlineId === serverId) {
        return true;
      }

      if (offlineMessage.temp_id && serverMessage.temp_id && offlineMessage.temp_id === serverMessage.temp_id) {
        return true;
      }

      return !!offlineSenderId && !!serverSenderId && offlineSenderId === serverSenderId && (
        ((offlineMessage.content || '') === (serverMessage.content || '') && closeInTime(offlineTime, serverTime)) ||
        attachmentsMatch(offlineMessage.attachments, serverMessage.attachments)
      );
    });
  });

  // Merge server messages with unresolved offline queue placeholders
  // Sort by created_at, falling back to timestamp for backwards compatibility
  const allMessages = [...messages, ...reconciledOfflineMessages].sort((a, b) => {
    const aTime = new Date(a.created_at || a.timestamp).getTime();
    const bTime = new Date(b.created_at || b.timestamp).getTime();
    return aTime - bTime;
  });

  // Scroll to bottom on new messages
  useEffect(() => {
    const scrollToBottom = () => {
      if (!messagesContainerRef.current) {
        return;
      }

      const container = messagesContainerRef.current;
      const latestRenderedMessage = allMessages[allMessages.length - 1];
      const isOwnLatestMessage = !!latestRenderedMessage && (
        latestRenderedMessage.sender_id === currentUserId ||
        latestRenderedMessage.sender_user_id === currentUserId ||
        latestRenderedMessage.pending
      );
      const renderedCount = allMessages.length;
      const previousCount = previousRenderedMessageCountRef.current;
      const hasNewMessage = renderedCount > previousCount;

      previousRenderedMessageCountRef.current = renderedCount;

      if (!hasScrolledToBottomRef.current && renderedCount > 0) {
        scrollThreadToBottom('auto');
      } else if (hasScrolledToBottomRef.current && hasNewMessage && (isOwnLatestMessage || wasNearBottomRef.current)) {
        scrollThreadToBottom(isOwnLatestMessage ? 'auto' : 'smooth');
      } else {
        wasNearBottomRef.current = container.scrollHeight - container.scrollTop - container.clientHeight < 120;
      }
    };

    const timeoutId = setTimeout(scrollToBottom, 150);
    return () => clearTimeout(timeoutId);
  }, [allMessages, currentUserId, scrollThreadToBottom]);
  
  // Filter messages by search
  const filteredMessages = searchQuery
    ? allMessages.filter(m => 
        m.content?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        m.sender_name?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : allMessages;
  
  // Group messages by date (fallback to timestamp for backwards compatibility)
  const messagesByDate = filteredMessages.reduce((groups, message) => {
    const date = new Date(message.created_at || message.timestamp).toLocaleDateString('en-ZA', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(message);
    return groups;
  }, {} as Record<string, ThreadMessage[]>);
  
  const isTeamThread = thread.thread_type === 'team';
  const isJobThread = isThreadTypeJob(thread.thread_type);
  const isScheduleThread = isThreadTypeSchedule(thread.thread_type);
  const isAiOperationsThread = isAiOperationsManagerThread(thread);
  const isAiProposalThread = isAiProposalManagerThread(thread);
  const specializedAiParticipantOption: SpecialisedAiParticipantOption | null = isAiOperationsThread
    ? AI_OPERATIONS_PARTICIPANT_OPTION
    : isAiProposalThread
      ? AI_PROPOSAL_PARTICIPANT_OPTION
      : null;
  const specializedAiThreadSenderName = specializedAiParticipantOption?.senderName || null;
  const specializedAiThreadLabel = specializedAiParticipantOption?.activeThreadLabel || null;
  const specializedAiThreadPlaceholder = specializedAiParticipantOption?.threadMessagePlaceholder || 'Type a message...';
  const participantCount = thread.members?.length || thread.member_count || 0;
  const isSystemThread = thread.thread_type === 'system' || thread.is_system_thread;
  const isOneToOneThread = thread.thread_type === 'direct' || (
    participantCount === 2
    && !isTeamThread
    && !isJobThread
    && !isScheduleThread
    && !isSystemThread
    && thread.thread_type !== 'total_operations'
  );
  // Format thread name
  const threadName = isOneToOneThread && thread.members
    ? thread.members.find(m => m.user_id !== currentUserId)?.display_name || thread.title
    : thread.title;
  
  // Get other member (for 1:1 threads)
  const otherMember = isOneToOneThread && thread.members
    ? thread.members.find(m => m.user_id !== currentUserId)
    : null;

  // Thread subtitle - show member count for group threads, nothing for 1:1 threads
  const threadClientLogo = thread.client_logo || thread.system_client_logo || clientLogo;
  const threadSubtitle = thread.thread_type === 'team'
    ? `Team · ${thread.members?.length || 0} members`
    : thread.thread_type === 'group'
    ? `${thread.members?.length || 0} members`
    : isJobThread
      ? `${thread.members?.length || 0} members`
      : isScheduleThread
        ? `${thread.members?.length || 0} members`
        : thread.thread_type === 'client'
          ? `${thread.members?.length || 0} members`
          : ''; // No subtitle for 1:1 threads

  // Helper to check if member should show online status
  const shouldShowMemberOnline = (member: ThreadMember) => {
    return member.show_when_online !== false; // Default to showing if not explicitly false
  };

  // Helper to check if member is online (last activity within 5 minutes)
  const isMemberOnline = (member: ThreadMember) => {
    if (!member.last_activity_at) return member.is_online || false;
    const lastActivity = new Date(member.last_activity_at).getTime();
    const now = Date.now();
    return (now - lastActivity) < 5 * 60 * 1000; // 5 minutes
  };

  return (
    <div 
      className={`relative flex flex-col overflow-hidden h-full ${
        isDark ? 'bg-gray-900' : 'bg-white'
      }`}
    >
      <div className={`sticky top-0 z-20 flex-shrink-0 backdrop-blur-sm ${isDark ? 'bg-gray-900/95' : 'bg-white/95'}`}>
        {/* Header - Sticky with accent bar (matches MessagesModalNew pattern) */}
        <div className={`border-b shadow-sm transition-[padding] duration-200 ${isDark ? 'bg-gray-800/95 border-gray-700' : 'bg-white/95 border-gray-200'}`}>
          {/* Theme-colored accent bar */}
          <div className="h-0.5" style={{ backgroundColor: isTeamThread && thread.team_color ? thread.team_color : primaryColor }} />

          {/* Top row - avatar, title, close button */}
          <div className="max-w-4xl mx-auto w-full px-3 py-2 flex items-center gap-3">
            {/* Avatar - use client logo for job/schedule/client threads, team icon for team threads */}
            <div className="relative">
              {isTeamThread ? (
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center font-medium"
                  style={{ backgroundColor: `${isTeamThread && thread.team_color ? thread.team_color : primaryColor}15`, color: isTeamThread && thread.team_color ? thread.team_color : primaryColor }}
                >
                  <UsersRound className="w-5 h-5" />
                </div>
              ) : isOneToOneThread && otherMember?.avatar_url ? (
                <img
                  src={otherMember.avatar_url}
                  alt={threadName}
                  className="w-10 h-10 rounded-full object-cover"
                />
              ) : ((isJobThread || isScheduleThread || thread.thread_type === 'client') && threadClientLogo) ? (
                <img
                  src={threadClientLogo}
                  alt={thread.thread_name}
                  className="w-10 h-10 rounded-full object-cover bg-white"
                />
              ) : thread.avatar_url ? (
                <img
                  src={thread.avatar_url}
                  alt={thread.thread_name}
                  className="w-10 h-10 rounded-full object-cover"
                />
              ) : (
                <div className="w-10 h-10 rounded-full flex items-center justify-center font-medium" style={{ backgroundColor: `${primaryColor}15`, color: primaryColor }}>
                  {thread.thread_type === 'group' || thread.thread_type === 'client' ? (
                    <Users className="w-5 h-5" />
                  ) : threadName?.charAt(0)?.toUpperCase() || '?'}
                </div>
              )}
              {isOneToOneThread && otherMember?.is_online && (
                <div className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-green-400 rounded-full border-2 ${isDark ? 'border-gray-800' : 'border-white'}`} />
              )}
            </div>
            
            {/* Title and subtitle */}
            <div className="flex-1 min-w-0">
              <h2 className={`font-semibold truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {thread.thread_name || threadName}
              </h2>
              <p className={`text-xs truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {typing.length > 0 ? (
                  <span style={{ color: primaryColor }}>
                    {typing.length === 1 
                      ? `${typing[0].userName} is typing...`
                      : `${typing.length} people typing...`}
                  </span>
                ) : isOneToOneThread ? '' : threadSubtitle}
              </p>
              {specializedAiThreadLabel && (
                <div className="mt-1 flex items-center gap-1.5">
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${
                    isDark ? 'bg-cyan-500/15 text-cyan-300' : 'bg-cyan-100 text-cyan-700'
                  }`}>
                    <Bot className="w-3 h-3" />
                    {specializedAiThreadLabel}
                  </span>
                </div>
              )}
            </div>
            
            {/* Action buttons */}
            <div className="flex items-center gap-1">
              {/* Call/Meeting button with dropdown (for team/group threads) */}
              {canMakeCalls && (onStartVoiceCall || onStartMeeting) && !isOneToOneThread && (thread.members?.length || 0) > 1 && (
                <div className="relative" ref={callMenuRef}>
                  <button
                    onClick={() => setShowCallMenu(!showCallMenu)}
                    className={`p-2 rounded-full transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
                    title="Call or start meeting"
                  >
                    <Phone className="w-5 h-5" />
                  </button>
                  
                  {/* Call/Meeting dropdown menu */}
                  {showCallMenu && (
                    <div className={`absolute right-0 top-full mt-1 w-52 rounded-lg shadow-xl z-50 overflow-hidden ${
                      isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
                    }`}>
                      {/* Call options - rings all participants */}
                      <div className={`px-3 py-1.5 text-xs font-medium ${isDark ? 'text-gray-400 bg-gray-900' : 'text-gray-500 bg-gray-50'}`}>
                        Call (rings everyone)
                      </div>
                      {onStartVoiceCall && (
                        <button
                          onClick={() => {
                            setShowCallMenu(false);
                            onStartVoiceCall();
                          }}
                          className={`w-full px-3 py-2.5 flex items-center gap-3 text-sm ${
                            isDark ? 'hover:bg-gray-700 text-white' : 'hover:bg-gray-100 text-gray-900'
                          }`}
                        >
                          <Phone className="w-4 h-4 text-green-500" />
                          Voice Call
                        </button>
                      )}
                      {onStartVideoCall && (
                        <button
                          onClick={() => {
                            setShowCallMenu(false);
                            onStartVideoCall();
                          }}
                          className={`w-full px-3 py-2.5 flex items-center gap-3 text-sm ${
                            isDark ? 'hover:bg-gray-700 text-white' : 'hover:bg-gray-100 text-gray-900'
                          }`}
                        >
                          <Video className="w-4 h-4 text-blue-500" />
                          Video Call
                        </button>
                      )}
                      
                      {/* Meeting options - notifies, doesn't ring */}
                      {onStartMeeting && (
                        <>
                          <div className={`px-3 py-1.5 text-xs font-medium border-t ${
                            isDark ? 'text-gray-400 bg-gray-900 border-gray-700' : 'text-gray-500 bg-gray-50 border-gray-200'
                          }`}>
                            Meeting (join when ready)
                          </div>
                          <button
                            onClick={() => {
                              setShowCallMenu(false);
                              onStartMeeting('voice');
                            }}
                            className={`w-full px-3 py-2.5 flex items-center gap-3 text-sm ${
                              isDark ? 'hover:bg-gray-700 text-white' : 'hover:bg-gray-100 text-gray-900'
                            }`}
                          >
                            <Calendar className="w-4 h-4 text-orange-500" />
                            Start Voice Meeting
                          </button>
                          <button
                            onClick={() => {
                              setShowCallMenu(false);
                              onStartMeeting('video');
                            }}
                            className={`w-full px-3 py-2.5 flex items-center gap-3 text-sm ${
                              isDark ? 'hover:bg-gray-700 text-white' : 'hover:bg-gray-100 text-gray-900'
                            }`}
                          >
                            <Calendar className="w-4 h-4 text-purple-500" />
                            Start Video Meeting
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              {/* Search */}
              <button
                onClick={() => setShowSearch(!showSearch)}
                className={`p-2 rounded-full transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
              >
                <Search className="w-5 h-5" />
              </button>
              
              {/* Settings button (for admins/owners) */}
              {onOpenSettings && (isAdmin || thread.my_permissions?.can_manage_thread) && (
                <button
                  onClick={onOpenSettings}
                  className={`p-2 rounded-full transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
                  title="Thread settings"
                >
                  <Settings className="w-5 h-5" />
                </button>
              )}
              
              {/* Menu */}
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setShowThreadMenu(!showThreadMenu)}
                  className={`p-2 rounded-full transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
                >
                  <MoreVertical className="w-5 h-5" />
                </button>
                
                {showThreadMenu && (
                  <div className={`absolute right-0 top-full mt-1 w-48 rounded-lg shadow-lg border z-[100] ${
                    isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                  }`}>
                    {onViewMembers && thread.thread_type !== 'direct' && (
                      <button
                        onClick={() => { onViewMembers(); setShowThreadMenu(false); }}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-left ${
                          isDark ? 'hover:bg-gray-700 text-gray-200' : 'hover:bg-gray-100 text-gray-700'
                        }`}
                      >
                        <Users className="w-4 h-4" />
                        <span className="text-sm">View Members</span>
                      </button>
                    )}
                    {onAddMember && thread.thread_type !== 'direct' && (isAdmin || thread.my_permissions?.can_manage_members) && (
                      <button
                        onClick={() => { onAddMember(); setShowThreadMenu(false); }}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-left ${
                          isDark ? 'hover:bg-gray-700 text-gray-200' : 'hover:bg-gray-100 text-gray-700'
                        }`}
                      >
                        <UserPlus className="w-4 h-4" />
                        <span className="text-sm">Add Member</span>
                      </button>
                    )}
                    {onMuteThread && (
                      <button
                        onClick={() => { onMuteThread(); setShowThreadMenu(false); }}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-left ${
                          isDark ? 'hover:bg-gray-700 text-gray-200' : 'hover:bg-gray-100 text-gray-700'
                        }`}
                      >
                        {thread.is_muted ? (
                          <>
                            <Bell className="w-4 h-4" />
                            <span className="text-sm">Unmute</span>
                          </>
                        ) : (
                          <>
                            <BellOff className="w-4 h-4" />
                            <span className="text-sm">Mute</span>
                          </>
                        )}
                      </button>
                    )}
                    
                    {/* Leave Thread - for non-direct threads when user has permission */}
                    {onLeaveThread && thread.thread_type !== 'direct' && (thread.my_permissions?.can_leave || isAdmin) && (
                      <button
                        onClick={() => { onLeaveThread(); setShowThreadMenu(false); }}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-left ${
                          isDark ? 'hover:bg-yellow-900/30 text-yellow-400' : 'hover:bg-yellow-50 text-yellow-700'
                        }`}
                      >
                        <LogOut className="w-4 h-4" />
                        <span className="text-sm">Leave Thread</span>
                      </button>
                    )}
                    
                    {/* Delete Thread - for admins/owners only */}
                    {onDeleteThread && (isAdmin || thread.my_permissions?.can_manage_thread) && (
                      <button
                        onClick={() => { onDeleteThread(); setShowThreadMenu(false); }}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-left ${
                          isDark ? 'hover:bg-red-900/30 text-red-400' : 'hover:bg-red-50 text-red-600'
                        }`}
                      >
                        <Trash2 className="w-4 h-4" />
                        <span className="text-sm">Delete Thread</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
              
              {/* Close button - X to go back to thread list */}
              <button
                onClick={onBack}
                className={`p-2 rounded-full transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
                title="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          {/* Member Avatars Row - For group, client, job, schedule threads */}
          {!isOneToOneThread && thread.members && thread.members.length > 0 && (
            <div className={`${isDark ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
              <div className="max-w-4xl mx-auto w-full px-3 py-2 flex items-center gap-1 overflow-x-auto scrollbar-hide">
                {thread.members.slice(0, 10).map(member => {
                  const isOnline = isMemberOnline(member);
                  const showOnline = shouldShowMemberOnline(member);
                  // Check for elevated role
                  const memberRole = (member as ThreadMemberWithOptionalRole).role?.toLowerCase();
                  const hasElevatedRole = ['owner', 'admin', 'supervisor'].includes(memberRole);
                  
                  return (
                    <button 
                      key={member.user_id}
                      onClick={() => setSelectedMemberDetails({
                        user_id: member.user_id,
                        display_name: member.display_name,
                        email: member.email,
                        avatar_url: member.avatar_url,
                        is_online: isOnline,
                        last_activity_at: member.last_activity_at,
                        role: memberRole
                      })}
                      className="relative flex-shrink-0 group cursor-pointer"
                      title={`${member.display_name}${showOnline ? (isOnline ? ' • Online' : ' • Offline') : ''}`}
                    >
                      {/* Role badge overlay */}
                      {hasElevatedRole && (
                        <div className={`absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-bold z-10 ${
                          memberRole === 'owner' ? 'bg-amber-500 text-white' :
                          memberRole === 'admin' ? 'bg-purple-500 text-white' :
                          'bg-blue-500 text-white'
                        }`} title={memberRole?.charAt(0).toUpperCase() + memberRole?.slice(1)}>
                          {memberRole === 'owner' ? '👑' : '⭐'}
                        </div>
                      )}
                      {member.avatar_url ? (
                        <img 
                          src={member.avatar_url} 
                          alt={member.display_name}
                          className={`w-8 h-8 rounded-full object-cover border-2 transition-transform group-hover:scale-110 ${
                            isDark ? 'border-gray-700' : 'border-white'
                          }`}
                        />
                      ) : (
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium text-white border-2 transition-transform group-hover:scale-110 ${
                          isDark ? 'bg-gray-600 border-gray-700' : 'bg-gray-400 border-white'
                        }`}>
                          {member.display_name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                      )}
                      {/* Online status indicator - respects member's show_when_online preference */}
                      {showOnline && (
                        <div className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 ${
                          isDark ? 'border-gray-800' : 'border-white'
                        } ${isOnline ? 'bg-green-500' : 'bg-gray-400'}`} />
                      )}
                    </button>
                  );
                })}
                {thread.members.length > 10 && (
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium border-2 flex-shrink-0 ${
                    isDark ? 'bg-gray-700 text-gray-300 border-gray-600' : 'bg-gray-200 text-gray-600 border-white'
                  }`}>
                    +{thread.members.length - 10}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        
        {/* Search bar (collapsible) */}
        {showSearch && (
          <div className={`border-b ${isDark ? 'border-gray-700 bg-gray-800/95' : 'border-gray-200 bg-white/95'}`}>
            <div className="max-w-4xl mx-auto w-full px-3 py-2">
              <div className="relative">
                <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search in conversation..."
                  className={`w-full pl-9 pr-8 py-2 rounded-lg text-sm ${
                    isDark ? 'bg-gray-700 text-gray-100 placeholder-gray-500' : 'bg-white text-gray-900 placeholder-gray-400'
                  } border ${isDark ? 'border-gray-600' : 'border-gray-300'}`}
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded ${isDark ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`}
                  >
                    <X className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                  </button>
                )}
              </div>
              {searchQuery && (
                <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {filteredMessages.length} results found
                </p>
              )}
            </div>
          </div>
        )}
        
        {/* Pinned Mentions Section - Stays at top until dismissed */}
        {pinnedMentions.length > 0 && (
          <div className={`border-b ${isDark ? 'bg-yellow-900/20 border-yellow-700/30' : 'bg-yellow-50 border-yellow-200'}`}>
            <div className="max-w-4xl mx-auto w-full px-4 py-2">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Pin className={`w-4 h-4 ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`} />
                  <span className={`text-sm font-medium ${isDark ? 'text-yellow-300' : 'text-yellow-700'}`}>
                    You were mentioned ({pinnedMentions.length})
                  </span>
                </div>
                {onDismissAllMentions && (
                  <button
                    onClick={onDismissAllMentions}
                    className={`text-xs px-2 py-1 rounded ${
                      isDark ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-200'
                    }`}
                  >
                    Dismiss all
                  </button>
                )}
              </div>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {pinnedMentions.slice(0, 5).map(mention => (
                  <div
                    key={mention.message_id}
                    className={`flex items-center justify-between gap-2 p-2 rounded cursor-pointer ${
                      isDark ? 'bg-gray-800/50 hover:bg-gray-800' : 'bg-white/50 hover:bg-white'
                    }`}
                    onClick={() => scrollToMessage(mention.message_id)}
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {mention.sender_avatar ? (
                        <img 
                          src={mention.sender_avatar} 
                          alt={mention.sender_name}
                          className="w-6 h-6 rounded-full object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                          isDark ? 'bg-gray-600 text-white' : 'bg-gray-300 text-gray-700'
                        } flex-shrink-0`}>
                          {mention.sender_name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className={`text-xs font-medium truncate ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          {mention.sender_name}
                        </p>
                        <p className={`text-xs truncate ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                          {mention.message_preview}
                        </p>
                      </div>
                    </div>
                    {onDismissMention && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDismissMention(mention.message_id);
                        }}
                        className={`p-1 rounded flex-shrink-0 ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}
                      >
                        <X className={`w-3 h-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                      </button>
                    )}
                  </div>
                ))}
                {pinnedMentions.length > 5 && (
                  <p className={`text-xs text-center py-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    +{pinnedMentions.length - 5} more mentions
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Messages area */}
      <div 
        ref={messagesContainerRef}
        className={`flex-1 min-h-0 overflow-y-auto overscroll-contain p-3 space-y-4 ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}
        style={{ scrollBehavior: 'smooth' }}
      >
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="w-8 h-8 border-3 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredMessages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
              isDark ? 'bg-gray-800' : 'bg-gray-200'
            }`}>
              <Users className={`w-8 h-8 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
            </div>
            <h3 className={`text-lg font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              No messages yet
            </h3>
            <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
              Start the conversation by sending a message
            </p>
          </div>
        ) : (
          Object.entries(messagesByDate).map(([date, msgs]) => (
            <div key={date}>
              {/* Date separator */}
              <div className="flex items-center justify-center my-4">
                <div className={`px-3 py-1 rounded-full text-xs ${
                  isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-200 text-gray-600'
                }`}>
                  {date}
                </div>
              </div>
              
              {/* Messages for this date */}
              <div className="space-y-1">
                {msgs.map((message, idx) => {
                  const isOwn = message.sender_id === currentUserId || message.sender_user_id === currentUserId;
                  const showAvatarForMsg = !isOwn && (
                    idx === 0 || 
                    (msgs[idx - 1]?.sender_id || msgs[idx - 1]?.sender_user_id) !== (message.sender_id || message.sender_user_id)
                  );
                  const messageId = message._id
                    || message.id
                    || (message as ThreadMessageWithLegacyId).message_id
                    || message.temp_id
                    || `${message.sender_user_id}-${message.timestamp || message.created_at || idx}`;
                  // Find member to get role
                  const member = thread.members?.find(
                    (m): m is ThreadMemberWithOptionalRole => m.user_id === message.sender_user_id,
                  );
                  const senderRole = member?.role;
                  
                  return (
                    <MessageBubble
                      key={messageId}
                      message={message}
                      isOwn={isOwn}
                      showAvatar={showAvatarForMsg && thread.thread_type !== 'direct'}
                      senderRole={senderRole}
                      searchQuery={searchQuery}
                      isDark={isDark}
                      currentUserId={currentUserId}
                      threadMemberCount={thread.members?.length || thread.member_count || 2}
                      onReply={() => handleReply(message)}
                      onReaction={onReaction}
                      onDelete={onDeleteMessage ? () => onDeleteMessage(messageId) : undefined}
                      onEdit={onEditMessage ? (newContent: string) => onEditMessage(messageId, newContent) : undefined}
                      onAddToTasks={onAddToTasks ? () => onAddToTasks(messageId) : undefined}
                      isInTasks={Boolean(hasTaskReference?.(messageId))}
                      isAddingToTask={addingTaskId === messageId}
                      onReviewAction={onReviewAction}
                      onOpenProposalEmail={canEmailProposalFromThread ? handleOpenProposalEmail : undefined}
                      proposalEmailLoadingId={proposalEmailLoadingId}
                      onImageClick={setSelectedImage}
                      onAvatarClick={(msg) => {
                        // Find the member in thread to get extended details
                        const clickedMember = thread.members?.find(m => m.user_id === msg.sender_user_id);
                        
                        // Check if this is an API-generated message
                        const isApiMessage = msg.is_api_message || msg.metadata?.sent_via === 'public_api';
                        
                        setSelectedMemberDetails({
                          user_id: msg.sender_user_id,
                          display_name: msg.sender_name,
                          email: isApiMessage ? undefined : (clickedMember?.email || msg.sender_email),
                          avatar_url: msg.sender_avatar || clickedMember?.avatar_url,
                          is_online: isApiMessage ? undefined : clickedMember?.is_online,
                          last_activity_at: isApiMessage ? undefined : clickedMember?.last_activity_at,
                          role: clickedMember?.role,
                          is_api_message: isApiMessage,
                          api_key_owner_name: isApiMessage ? clickedMember?.display_name : undefined,
                          api_key_owner_email: isApiMessage ? clickedMember?.email : undefined
                        });
                      }}
                    />
                  );
                })}
              </div>
            </div>
          ))
        )}

        {aiParticipantThinking && specializedAiThreadSenderName && (
          <div className="flex items-center gap-2 px-3">
            <div className="flex gap-1">
              <div className={`w-2 h-2 rounded-full animate-bounce ${isDark ? 'bg-cyan-400' : 'bg-cyan-500'}`} style={{ animationDelay: '0ms' }} />
              <div className={`w-2 h-2 rounded-full animate-bounce ${isDark ? 'bg-cyan-400' : 'bg-cyan-500'}`} style={{ animationDelay: '150ms' }} />
              <div className={`w-2 h-2 rounded-full animate-bounce ${isDark ? 'bg-cyan-400' : 'bg-cyan-500'}`} style={{ animationDelay: '300ms' }} />
            </div>
            <span className={`text-xs ${isDark ? 'text-cyan-300' : 'text-cyan-700'}`}>
              {specializedAiThreadSenderName} is reviewing the thread…
            </span>
          </div>
        )}
        
        {/* Typing indicator */}
        {typing.length > 0 && (
          <div className="flex items-center gap-2 px-3">
            <div className="flex gap-1">
              <div className={`w-2 h-2 rounded-full animate-bounce ${isDark ? 'bg-gray-500' : 'bg-gray-400'}`} style={{ animationDelay: '0ms' }} />
              <div className={`w-2 h-2 rounded-full animate-bounce ${isDark ? 'bg-gray-500' : 'bg-gray-400'}`} style={{ animationDelay: '150ms' }} />
              <div className={`w-2 h-2 rounded-full animate-bounce ${isDark ? 'bg-gray-500' : 'bg-gray-400'}`} style={{ animationDelay: '300ms' }} />
            </div>
            <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              {typing.length === 1 
                ? `${typing[0].userName} is typing...`
                : `${typing.map(t => t.userName).join(', ')} are typing...`}
            </span>
          </div>
        )}
        
        {/* Scroll anchor with extra padding to ensure full scroll */}
        <div ref={messagesEndRef} className="h-4" />
      </div>
      
      {/* Message input area */}
      <MessageInput
          value={messageText}
          onChange={setMessageText}
          onSend={handleSend}
          onAttachmentUpload={handleAttachmentUpload}
          onComposerFocus={handleComposerFocus}
          isDark={isDark}
          disabled={false}
          sending={sending}
          placeholder={specializedAiThreadPlaceholder}
          pendingAttachments={pendingAttachments}
          onRemoveAttachment={handleRemoveAttachment}
          uploadingAttachment={uploadingAttachment}
          replyTo={replyTo}
          onCancelReply={() => setReplyTo(null)}
          onVoiceNote={onSendVoiceNote ? handleVoiceNote : undefined}
          onShareLocation={onShareLocation}
          sharingLocation={sharingLocation}
          members={thread.members || []}
          onMention={(userId) => setSelectedMentions(prev => prev.includes(userId) ? prev : [...prev, userId])}
          onTyping={onTyping}
          onStoppedTyping={onStoppedTyping}
          enableSpeechToText={!onSendVoiceNote}
        />
      
      {/* Image viewer modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
          >
            <X className="w-6 h-6" />
          </button>
          <img 
            src={selectedImage}
            alt="Full size"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {proposalToEmail && (
        <ProposalSendDialog
          isOpen={Boolean(proposalToEmail)}
          proposal={proposalToEmail}
          allowedSendMethods={['email']}
          initialSendMethod="email"
          onClose={() => setProposalToEmail(null)}
          onSent={(updatedProposal) => {
            setProposalToEmail(updatedProposal);
          }}
        />
      )}
      
      {/* Member Details Modal */}
      <MemberDetailsModal
        isOpen={!!selectedMemberDetails}
        onClose={() => setSelectedMemberDetails(null)}
        member={selectedMemberDetails}
        threadType={thread.thread_type}
        isCurrentUser={selectedMemberDetails?.user_id === currentUserId}
        isDark={isDark}
        onStartVoiceCall={onStartVoiceCall ? (userId) => {
          setSelectedMemberDetails(null);
          // Find member and start call
          const member = thread.members?.find(m => m.user_id === userId);
          if (member) onStartVoiceCall();
        } : undefined}
        onStartVideoCall={onStartVideoCall ? (userId) => {
          setSelectedMemberDetails(null);
          const member = thread.members?.find(m => m.user_id === userId);
          if (member) onStartVideoCall();
        } : undefined}
      />
    </div>
  );
}
